import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** Resolve payee (company or first assigned cleaner) for agreement. */
export async function resolvePropertyPayee(
  propertyId: string,
  mode: "company" | "solo"
): Promise<{ payee_type: PayeeType; payee_id: string; payee_name: string | null } | null> {
  if (mode === "company") {
    const { data: pca } = await supabase
      .from("property_cleaning_assignments")
      .select("company_id")
      .eq("property_id", propertyId)
      .maybeSingle();
    const companyId = (pca as { company_id?: string } | null)?.company_id;
    if (!companyId) return null;
    const { data: company } = await supabase
      .from("cleaning_companies")
      .select("id, company_name")
      .eq("id", companyId)
      .maybeSingle();
    return {
      payee_type: "company",
      payee_id: companyId,
      payee_name: (company as { company_name?: string } | null)?.company_name ?? null,
    };
  }
  const { data: assignments } = await supabase
    .from("cleaner_assignments")
    .select("cleaner_id")
    .eq("property_id", propertyId)
    .limit(1);
  const cleanerId = (assignments ?? [])[0] as { cleaner_id?: string } | undefined;
  const cid = cleanerId?.cleaner_id;
  if (!cid) return null;
  const { data: cleaner } = await supabase
    .from("cleaners")
    .select("id, name")
    .eq("id", cid)
    .maybeSingle();
  return {
    payee_type: "cleaner",
    payee_id: cid,
    payee_name: (cleaner as { name?: string } | null)?.name ?? null,
  };
}

export type PayeeType = "company" | "cleaner";
export type PaymentMode = "on_app" | "off_app";
export type PayoutSchedule = "per_cleaning" | "weekly" | "biweekly" | "monthly";

export type AgreementCurrency = "CAD" | "USD";

export interface PropertyCleaningAgreement {
  id: string;
  property_id: string;
  payee_type: PayeeType;
  payee_id: string;
  default_rate_cents: number;
  /** ISO 4217 currency code for rates and invoice display. Defaults to CAD. */
  currency: AgreementCurrency | string;
  payment_mode: PaymentMode;
  payout_schedule: PayoutSchedule;
  inventory_included: boolean;
  inventory_approval_required: boolean;
  created_at: string;
  updated_at: string;
}

export function usePropertyCleaningAgreement(propertyId: string | undefined) {
  return useQuery({
    queryKey: ["property-cleaning-agreement", propertyId],
    queryFn: async (): Promise<PropertyCleaningAgreement | null> => {
      if (!propertyId) return null;
      const { data, error } = await supabase
        .from("property_cleaning_agreements")
        .select("*")
        .eq("property_id", propertyId)
        .maybeSingle();
      if (error) {
        if (error.code === "42P01") return null;
        throw error;
      }
      const row = data as (PropertyCleaningAgreement & { currency?: string | null }) | null;
      if (row && (row.currency == null || row.currency === "")) {
        row.currency = "CAD";
      }
      return row as PropertyCleaningAgreement | null;
    },
    enabled: !!propertyId,
  });
}

export function useUpsertPropertyCleaningAgreement(propertyId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      payee_type: PayeeType;
      payee_id: string;
      default_rate_cents: number;
      currency?: AgreementCurrency | string;
      payment_mode?: PaymentMode;
      payout_schedule?: PayoutSchedule;
      inventory_included?: boolean;
      inventory_approval_required?: boolean;
    }) => {
      if (!propertyId) throw new Error("Property ID required");
      const { data, error } = await supabase
        .from("property_cleaning_agreements")
        .upsert(
          {
            property_id: propertyId,
            payee_type: payload.payee_type,
            payee_id: payload.payee_id,
            default_rate_cents: Math.max(0, payload.default_rate_cents),
            currency: payload.currency ?? "CAD",
            payment_mode: payload.payment_mode ?? "off_app",
            payout_schedule: payload.payout_schedule ?? "monthly",
            inventory_included: payload.inventory_included ?? true,
            inventory_approval_required: payload.inventory_approval_required ?? true,
          },
          { onConflict: "property_id" }
        )
        .select()
        .single();
      if (error) throw error;
      return data as PropertyCleaningAgreement;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-cleaning-agreement", propertyId] });
    },
  });
}
